import { useState } from "react";
import axios from "axios";
import { API_BASE } from "@config/env";
import { Modal } from "@common/Modal";

const fmt = (n) => {
  if (!n) return "0";
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000)    return (n / 1000).toFixed(1) + "K";
  return Number(n).toLocaleString();
};

const CALL_LABELS = { search_call1: "Search — pass 1", search_call2: "Search — pass 2", profile_chat: "Profile chat" };
const CALL_PILL   = {
  search_call1: { background: "var(--fc-primary-light)", color: "#2563EB" },
  search_call2: { background: "#111827", color: "#fff"    },
  profile_chat: { background: "var(--fc-green-light)", color: "var(--fc-green-text)" },
};
const RANGES = [{ label: "7d", value: "7" }, { label: "30d", value: "30" }, { label: "90d", value: "90" }, { label: "All", value: "all" }];

export default function ConnectIQUsageModal({ userSlug, onClose }) {
  const [range,    setRange]    = useState("30");
  const [loading,  setLoading]  = useState(false);
  const [data,     setData]     = useState(null);

  const load = async (r) => {
    setLoading(true);
    const fd = new FormData();
    fd.append("user_slug", userSlug);
    fd.append("range", r);
    const res = await axios.post(`${API_BASE}connectiq/get_firm_usage`, fd).then(x => x.data).catch(() => null);
    if (res?.status === 1) setData(res);
    setLoading(false);
  };

  const changeRange = (r) => { setRange(r); load(r); };

  const title = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 32, height: 32, borderRadius: 9, background: "#111827", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}><i className="fa-solid fa-robot" style={{fontSize:13}} /></div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fc-text-primary)" }}>ConnectIQ Usage</div>
        <div style={{ fontSize: 11, color: "var(--fc-text-subtle)" }}>Token usage by type</div>
      </div>
    </div>
  );

  return (
    <Modal title={title} onClose={onClose} maxWidth={560}>
      {/* Range pills */}
      <div style={{ display: "flex", gap: 5, marginBottom: 20 }}>
        {RANGES.map(r => (
          <button key={r.value} onClick={() => changeRange(r.value)}
            style={{ padding: "5px 14px", fontSize: 12, borderRadius: 99, border: "1px solid var(--fc-border)", background: range === r.value ? "#111827" : "var(--fc-card-bg)", color: range === r.value ? "#fff" : "var(--fc-text-muted)", cursor: "pointer", fontFamily: "inherit", fontWeight: range === r.value ? 700 : 400, transition: "all 0.15s" }}>
            {r.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "32px 0", color: "var(--fc-text-subtle)", fontSize: 13 }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading usage data...
        </div>
      ) : !data?.totals || data.totals.call_count === 0 ? (
        <div style={{ textAlign: "center", padding: "32px 0", color: "var(--fc-text-subtle)", fontSize: 13 }}>No usage recorded in this period.</div>
      ) : (
        <>
          {/* Stat row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 20 }}>
            {[
              { label: "Total calls",   value: fmt(data.totals.call_count)  },
              { label: "Total tokens",  value: fmt(data.totals.total_tokens) },
              { label: "Input tokens",  value: fmt(data.totals.total_input)  },
            ].map((s, i) => (
              <div key={i} style={{ background: "var(--fc-bg-light)", borderRadius: 10, padding: "12px 14px", border: "1px solid var(--fc-border-light)" }}>
                <div style={{ fontSize: 10, color: "var(--fc-text-subtle)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>{s.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "var(--fc-text-primary)", lineHeight: 1 }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div style={{ borderTop: "1px solid var(--fc-border-light)", marginBottom: 16 }} />

          {/* Breakdown */}
          {data.breakdown?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {data.breakdown.map((row, i) => {
                const pill = CALL_PILL[row.call_type] || { background: "var(--fc-border-light)", color: "var(--fc-text-secondary)" };
                const pct  = data.totals.total_tokens > 0 ? Math.round((row.total_tokens / data.totals.total_tokens) * 100) : 0;
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "var(--fc-bg-light)", borderRadius: 10, border: "1px solid var(--fc-border-light)" }}>
                    <span style={{ fontSize: 10, padding: "3px 10px", borderRadius: 99, fontWeight: 700, flexShrink: 0, minWidth: 120, textAlign: "center", ...pill }}>
                      {CALL_LABELS[row.call_type] || row.call_type}
                    </span>
                    <div style={{ flex: 1, height: 5, background: "var(--fc-border)", borderRadius: 99, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: "#2563EB", borderRadius: 99 }} />
                    </div>
                    <div style={{ display: "flex", gap: 16, flexShrink: 0 }}>
                      {[{ label: "Calls", value: fmt(row.call_count) }, { label: "Tokens", value: fmt(row.total_tokens) }].map((m, mi) => (
                        <div key={mi} style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 10, color: "var(--fc-text-subtle)" }}>{m.label}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--fc-text-primary)" }}>{m.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}